import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface NavbarProps {
  scrolled: boolean;
}

export default function Navbar({ scrolled }: NavbarProps) {
  const [platformsOpen, setPlatformsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const handleNav = (path: string) => {
    setMobileOpen(false);
    navigate(path);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="/" onClick={(e) => { e.preventDefault(); handleNav('/'); }} className="flex items-center cursor-pointer flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/6d0943092329599386bdfbaff4198b68/3843b12adf664db03f0d37a8ef9e817d.png"
              alt="RDM Security Logo"
              className="h-16 sm:h-18 md:h-20 w-auto"
            />
          </a>

          {/* Center Navigation — desktop only */}
          <div className="hidden lg:flex items-center space-x-10">
            <a
              href="#solutions"
              className={`text-sm font-light tracking-wider transition-colors cursor-pointer whitespace-nowrap ${
                scrolled ? 'text-gray-700 hover:text-[#4A90E2]' : 'text-white/90 hover:text-[#4A90E2]'
              }`}
            >
              SOLUTIONS
            </a>
            <div
              className="relative"
              onMouseEnter={() => setPlatformsOpen(true)}
              onMouseLeave={() => setPlatformsOpen(false)}
            >
              <button className={`text-sm font-light tracking-wider transition-colors flex items-center gap-1 whitespace-nowrap ${
                scrolled ? 'text-gray-700 hover:text-[#4A90E2]' : 'text-white/90 hover:text-[#4A90E2]'
              }`}>
                PLATFORMS
                <i className="ri-arrow-down-s-line text-base"></i>
              </button>
              {platformsOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-white/95 backdrop-blur-md rounded-lg shadow-xl py-2 border border-gray-200">
                  <a href="#control4" className="block px-4 py-2 text-sm text-gray-700 hover:text-[#4A90E2] hover:bg-gray-50 cursor-pointer">Control4</a>
                  <a href="#savant" className="block px-4 py-2 text-sm text-gray-700 hover:text-[#4A90E2] hover:bg-gray-50 cursor-pointer">Savant</a>
                  <a href="#rti" className="block px-4 py-2 text-sm text-gray-700 hover:text-[#4A90E2] hover:bg-gray-50 cursor-pointer">RTI</a>
                  <a href="#brilliant" className="block px-4 py-2 text-sm text-gray-700 hover:text-[#4A90E2] hover:bg-gray-50 cursor-pointer">Brilliant</a>
                </div>
              )}
            </div>
            <a
              href="/services"
              onClick={(e) => { e.preventDefault(); handleNav('/services'); }}
              className={`text-sm font-light tracking-wider transition-colors cursor-pointer whitespace-nowrap ${
                scrolled ? 'text-gray-700 hover:text-[#4A90E2]' : 'text-white/90 hover:text-[#4A90E2]'
              }`}
            >
              SERVICES
            </a>
            <a
              href="/about"
              onClick={(e) => { e.preventDefault(); handleNav('/about'); }}
              className={`text-sm font-light tracking-wider transition-colors cursor-pointer whitespace-nowrap ${
                scrolled ? 'text-gray-700 hover:text-[#4A90E2]' : 'text-white/90 hover:text-[#4A90E2]'
              }`}
            >
              ABOUT
            </a>
            <a
              href="/contact"
              onClick={(e) => { e.preventDefault(); handleNav('/contact'); }}
              className={`text-sm font-light tracking-wider transition-colors cursor-pointer whitespace-nowrap ${
                scrolled ? 'text-gray-700 hover:text-[#4A90E2]' : 'text-white/90 hover:text-[#4A90E2]'
              }`}
            >
              CONTACT
            </a>
          </div>

          {/* Right Side — desktop */}
          <div className="hidden lg:flex items-center gap-6">
            <button className={`w-5 h-5 flex items-center justify-center transition-colors cursor-pointer ${
              scrolled ? 'text-gray-700 hover:text-[#4A90E2]' : 'text-white/80 hover:text-[#4A90E2]'
            }`}>
              <i className="ri-search-line text-xl"></i>
            </button>
            <button className={`w-5 h-5 flex items-center justify-center transition-colors cursor-pointer ${
              scrolled ? 'text-gray-700 hover:text-[#4A90E2]' : 'text-white/80 hover:text-[#4A90E2]'
            }`}>
              <i className="ri-user-line text-xl"></i>
            </button>
            <a
              href="#schedule"
              className="px-6 py-3 bg-[#4A90E2] text-white text-sm font-medium tracking-wide rounded-full hover:bg-[#3A7BC8] hover:shadow-lg hover:shadow-[#4A90E2]/30 transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap"
            >
              SCHEDULE SURVEY
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>

          {/* Hamburger — mobile only */}
          <button
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              scrolled || mobileOpen ? 'text-gray-800' : 'text-white'
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-2xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white/98 backdrop-blur-md border-t border-gray-100 px-4 py-6 space-y-1 shadow-xl">
          <a href="#solutions" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-sm font-medium tracking-wider text-gray-700 hover:text-[#4A90E2] hover:bg-gray-50 rounded-lg cursor-pointer">
            SOLUTIONS
          </a>
          <div>
            <button
              onClick={() => setPlatformsOpen(!platformsOpen)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium tracking-wider text-gray-700 hover:text-[#4A90E2] hover:bg-gray-50 rounded-lg cursor-pointer"
            >
              PLATFORMS
              <i className={`ri-arrow-down-s-line text-base transition-transform ${platformsOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {platformsOpen && (
              <div className="ml-4 mt-1 space-y-1">
                <a href="#control4" onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-sm text-gray-600 hover:text-[#4A90E2] hover:bg-gray-50 rounded-lg cursor-pointer">Control4</a>
                <a href="#savant" onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-sm text-gray-600 hover:text-[#4A90E2] hover:bg-gray-50 rounded-lg cursor-pointer">Savant</a>
                <a href="#rti" onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-sm text-gray-600 hover:text-[#4A90E2] hover:bg-gray-50 rounded-lg cursor-pointer">RTI</a>
                <a href="#brilliant" onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-sm text-gray-600 hover:text-[#4A90E2] hover:bg-gray-50 rounded-lg cursor-pointer">Brilliant</a>
              </div>
            )}
          </div>
          <a href="/services" onClick={(e) => { e.preventDefault(); handleNav('/services'); }} className="block px-4 py-3 text-sm font-medium tracking-wider text-gray-700 hover:text-[#4A90E2] hover:bg-gray-50 rounded-lg cursor-pointer">
            SERVICES
          </a>
          <a href="/about" onClick={(e) => { e.preventDefault(); handleNav('/about'); }} className="block px-4 py-3 text-sm font-medium tracking-wider text-gray-700 hover:text-[#4A90E2] hover:bg-gray-50 rounded-lg cursor-pointer">
            ABOUT
          </a>
          <a href="/contact" onClick={(e) => { e.preventDefault(); handleNav('/contact'); }} className="block px-4 py-3 text-sm font-medium tracking-wider text-gray-700 hover:text-[#4A90E2] hover:bg-gray-50 rounded-lg cursor-pointer">
            CONTACT
          </a>
          <div className="pt-4 border-t border-gray-100">
            <a
              href="#schedule"
              onClick={() => setMobileOpen(false)}
              className="w-full px-6 py-3 bg-[#4A90E2] text-white text-sm font-medium tracking-wide rounded-full hover:bg-[#3A7BC8] transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              SCHEDULE SURVEY
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
